import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, CheckCircle, AlertCircle, Loader2, Save, Sparkles,
  FileText, BookOpen, X, Info
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useToast } from './ui';

interface Candidate {
  session_id: string;
  candidate_code: string;
  anonymous_id: string;
  status: string;
  started_at: string;
  submitted_at: string | null;
  exam_title: string;
  percentage: number | null;
  passed: boolean | null;
  grade: string | null;
  total_answers: number;
  graded_answers: number;
}

interface Answer {
  answer_id: string;
  question_text: string;
  question_type: string;
  max_points: number;
  candidate_answer: string;
  selected_option: number | null;
  options?: Array<{ text: string; is_correct: boolean }>;
  grading_criteria?: string;
  sensor_score: number | null;
  sensor_feedback: string | null;
  ai_suggested_score: number | null;
  ai_suggested_feedback: string | null;
  ai_model: string | null;
}

interface CourseContext {
  course_id: string;
  course_name: string;
  start_date: string;
  end_date: string;
  exam_set_id: string;
  exam_title: string;
  exam_description: string;
  pass_threshold: number;
  study_plan_id: string;
  study_plan_title: string;
  ects_credits: number;
  estimated_hours: number;
  learning_outcomes: {
    knowledge: string[];
    skills: string[];
    competence: string[];
  };
  content_modules: Array<{
    title: string;
    description: string;
    hours: number;
  }>;
  assessment_criteria: string;
  metadata: any;
  total_questions: number;
  total_points: number;
}

interface QuestionStatistics {
  question_id: string;
  question_text: string;
  question_type: string;
  max_points: number;
  total_answers: number;
  graded_count: number;
  avg_score: string | null;
  min_score: string | null;
  max_score: string | null;
  std_dev: string | null;
}

// Sensor grading interface with KI assistance
export default function SensorGrading() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [currentAnswerIndex, setCurrentAnswerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [courseContext, setCourseContext] = useState<CourseContext | null>(null);
  const [showContextModal, setShowContextModal] = useState(false);
  const [statistics, setStatistics] = useState<QuestionStatistics[]>([]);

  useEffect(() => {
    if (courseId) {
      fetchCandidates();
      fetchCourseContext();
      fetchStatistics();
    }
  }, [courseId]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/sensor/candidates/${courseId}`);
      const data = await response.json();

      if (data.success) {
        setCandidates(data.candidates);
      } else {
        setError(data.message || 'Kunne ikke hente kandidater');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseContext = async () => {
    try {
      const response = await authenticatedFetch(`/api/sensor/course/${courseId}/context`);
      const data = await response.json();

      if (data.success) {
        setCourseContext(data.context);
      } else {
        console.error('Could not fetch course context:', data.message);
      }
    } catch (err: any) {
      console.error('Error fetching course context:', err.message);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await authenticatedFetch(`/api/sensor/course/${courseId}/statistics`);
      const data = await response.json();

      if (data.success) {
        setStatistics(data.statistics);
      } else {
        console.error('Could not fetch statistics:', data.message);
      }
    } catch (err: any) {
      console.error('Error fetching statistics:', err.message);
    }
  };

  const fetchAnswers = async (sessionId: string) => {
    try {
      setLoadingAnswers(true);
      const response = await authenticatedFetch(`/api/sensor/session/${sessionId}/answers`);
      const data = await response.json();

      if (data.success) {
        setAnswers(data.answers);
      } else {
        setError(data.message || 'Kunne ikke hente svar');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingAnswers(false);
    }
  };

  const selectCandidate = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setCurrentAnswerIndex(0);
    fetchAnswers(candidate.session_id);
  };

  const goToNextAnswer = () => {
    if (currentAnswerIndex < answers.length - 1) {
      setCurrentAnswerIndex(currentAnswerIndex + 1);
    }
  };

  const goToPreviousAnswer = () => {
    if (currentAnswerIndex > 0) {
      setCurrentAnswerIndex(currentAnswerIndex - 1);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedCandidate || answers.length === 0) return;

      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement.tagName !== 'INPUT' && activeElement.tagName !== 'TEXTAREA') {
          e.preventDefault();
          goToNextAnswer();
        }
      } else if (e.key === 'p' && !e.ctrlKey && !e.metaKey) {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement.tagName !== 'INPUT' && activeElement.tagName !== 'TEXTAREA') {
          e.preventDefault();
          goToPreviousAnswer();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedCandidate, answers, currentAnswerIndex]);

  const handleGenerateAI = async () => {
    if (!selectedCandidate) return;

    try {
      setGeneratingAI(true);
      setError('');
      const response = await authenticatedFetch(
        `/api/sensor/generate-ai-grading/${selectedCandidate.session_id}`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setSuccess(`KI-sensurutkast generert for ${data.graded} svar`);
        fetchAnswers(selectedCandidate.session_id);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Kunne ikke generere KI-sensurutkast');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSaveGrading = async (answerId: string, score: number, feedback: string, acceptAI: boolean) => {
    try {
      setError('');
      const response = await authenticatedFetch(`/api/sensor/save-grading/${answerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          score,
          feedback,
          accept_ai_suggestion: acceptAI
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Sensurering lagret');
        if (selectedCandidate) {
          fetchAnswers(selectedCandidate.session_id);
          fetchCandidates(); // Refresh counts
        }
        if (data.all_graded) {
          setSuccess('Alle svar sensurert! Prøven er nå fullført.');
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Kunne ikke lagre sensurering');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/sensor/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sensurering</h2>
          <p className="text-sm text-gray-600">Gjennomgå og sensurer kandidatbesvarelser</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Candidate list */}
        <div className="lg:col-span-1">
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900">Kandidater</h3>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {candidates.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  Ingen kandidater funnet
                </div>
              ) : (
                candidates.map((candidate) => {
                  const progress = candidate.total_answers > 0
                    ? (candidate.graded_answers / candidate.total_answers) * 100
                    : 0;

                  return (
                    <div
                      key={candidate.session_id}
                      onClick={() => selectCandidate(candidate)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedCandidate?.session_id === candidate.session_id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-sm">
                            {candidate.candidate_code}
                          </span>
                        </div>
                        {candidate.status === 'submitted' && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            Venter
                          </span>
                        )}
                        {candidate.status === 'graded' && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                            Sensurert
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-gray-500 mb-2">
                        Innlevert: {candidate.submitted_at
                          ? new Date(candidate.submitted_at).toLocaleString('nb-NO')
                          : 'Pågår'}
                      </div>

                      {candidate.total_answers > 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Fremgang</span>
                            <span>{candidate.graded_answers}/{candidate.total_answers}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Grading panel */}
        <div className="lg:col-span-2">
          {!selectedCandidate ? (
            <div className="bg-white border rounded-lg p-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Velg en kandidat
              </h3>
              <p className="text-gray-600">
                Velg en kandidat fra listen for å starte sensurering
              </p>
            </div>
          ) : loadingAnswers ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Candidate info and context */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">
                      {selectedCandidate.candidate_code}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedCandidate.exam_title}
                    </p>
                  </div>
                  <button
                    onClick={handleGenerateAI}
                    disabled={generatingAI}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {generatingAI ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Lag KI-sensurutkast
                  </button>
                </div>
                {courseContext && (
                  <div className="pt-3 border-t">
                    <button
                      onClick={() => setShowContextModal(true)}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span>Vis studieplan og sensureringskriterier</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Question Overview */}
              {answers.length > 0 && (
                <div className="bg-white border rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Spørsmålsoversikt</h4>
                  <div className="flex flex-wrap gap-2">
                    {answers.map((ans, idx) => {
                      const isGraded = ans.sensor_score !== null;
                      const isCurrent = idx === currentAnswerIndex;
                      return (
                        <button
                          key={ans.answer_id}
                          onClick={() => setCurrentAnswerIndex(idx)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition ${
                            isCurrent
                              ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                              : isGraded
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={`Spørsmål ${idx + 1} - ${isGraded ? 'Sensurert' : 'Ikke sensurert'}`}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1 mr-3">
                      <span className="w-3 h-3 bg-green-100 border border-green-300 rounded"></span>
                      Sensurert
                    </span>
                    <span className="inline-flex items-center gap-1 mr-3">
                      <span className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></span>
                      Ikke sensurert
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-3 h-3 bg-blue-600 border border-blue-600 rounded"></span>
                      Aktivt
                    </span>
                  </div>
                </div>
              )}

              {/* Answer Navigation */}
              {answers.length > 0 && (
                <div className="bg-white border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">
                        Spørsmål {currentAnswerIndex + 1} av {answers.length}
                      </span>
                      <span className="text-xs text-gray-500">
                        Trykk 'N' for neste, 'P' for forrige
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={goToPreviousAnswer}
                        disabled={currentAnswerIndex === 0}
                        className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← Forrige
                      </button>
                      <button
                        onClick={goToNextAnswer}
                        disabled={currentAnswerIndex === answers.length - 1}
                        className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Neste →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Answer */}
              {answers[currentAnswerIndex] && (
                <AnswerGradingCard
                  key={answers[currentAnswerIndex].answer_id}
                  answer={answers[currentAnswerIndex]}
                  index={currentAnswerIndex}
                  onSave={handleSaveGrading}
                  statistics={statistics}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Context Modal */}
      {showContextModal && courseContext && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Studieplan og Sensureringskriterier</h2>
              <button
                onClick={() => setShowContextModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Exam Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="h-5 w-5 text-blue-600" />
                  Prøveinformasjon
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Prøvetittel:</p>
                      <p className="text-blue-900">{courseContext.exam_title}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Beståelsesgrense:</p>
                      <p className="text-blue-900">{courseContext.pass_threshold}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Antall spørsmål:</p>
                      <p className="text-blue-900">{courseContext.total_questions}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Totale poeng:</p>
                      <p className="text-blue-900">{courseContext.total_points}</p>
                    </div>
                  </div>
                  {courseContext.exam_description && (
                    <div className="mt-3 pt-3 border-t border-blue-300">
                      <p className="text-sm text-blue-700 font-medium mb-1">Beskrivelse:</p>
                      <p className="text-blue-900">{courseContext.exam_description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Study Plan Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-green-600" />
                  Studieplan
                </h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm text-green-700 font-medium">Tittel:</p>
                    <p className="text-green-900 font-semibold">{courseContext.study_plan_title}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-green-700 font-medium">Kompetansepoeng:</p>
                      <p className="text-green-900">{courseContext.ects_credits} studiepoeng</p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700 font-medium">Estimerte timer:</p>
                      <p className="text-green-900">{courseContext.estimated_hours} timer</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Learning Outcomes */}
              {courseContext.learning_outcomes && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Læringsutbytte</h3>
                  <div className="space-y-4">
                    {courseContext.learning_outcomes.knowledge?.length > 0 && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-purple-900 mb-2">Kunnskap:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-purple-800">
                          {courseContext.learning_outcomes.knowledge.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {courseContext.learning_outcomes.skills?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-amber-900 mb-2">Ferdigheter:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                          {courseContext.learning_outcomes.skills.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {courseContext.learning_outcomes.competence?.length > 0 && (
                      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-cyan-900 mb-2">Generell kompetanse:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-cyan-800">
                          {courseContext.learning_outcomes.competence.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assessment Criteria */}
              {courseContext.assessment_criteria && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Vurderingskriterier</h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">
                      {courseContext.assessment_criteria}
                    </p>
                  </div>
                </div>
              )}

              {/* Content Modules */}
              {courseContext.content_modules?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Innholdsmoduler</h3>
                  <div className="space-y-3">
                    {courseContext.content_modules.map((module, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-gray-900">{module.title}</p>
                          <span className="text-sm text-gray-600">{module.hours} timer</span>
                        </div>
                        <p className="text-sm text-gray-700">{module.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end">
              <button
                onClick={() => setShowContextModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Answer grading card component
function AnswerGradingCard({
  answer,
  index,
  onSave,
  statistics
}: {
  answer: Answer;
  index: number;
  onSave: (answerId: string, score: number, feedback: string, acceptAI: boolean) => void;
  statistics: QuestionStatistics[];
}) {
  const toast = useToast();
  // Find statistics for this question
  const questionStats = statistics.find(s => s.question_text === answer.question_text);
  // Auto-score for objective questions
  const getAutoScore = () => {
    if (answer.question_type === 'multiple_choice' || answer.question_type === 'true_false') {
      if (answer.selected_option !== null && answer.options) {
        const isCorrect = answer.options[answer.selected_option]?.is_correct;
        return isCorrect ? answer.max_points : 0;
      }
    }
    return null;
  };

  const autoScore = getAutoScore();
  const initialScore = answer.sensor_score?.toString() || (autoScore !== null ? autoScore.toString() : '');

  const [score, setScore] = useState(initialScore);
  const [feedback, setFeedback] = useState(answer.sensor_feedback || '');
  const [useAI, setUseAI] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | null>(null);

  const isGraded = answer.sensor_score !== null;
  const hasAI = answer.ai_suggested_score !== null;
  const isObjectiveQuestion = answer.question_type === 'multiple_choice' || answer.question_type === 'true_false';
  const isAutoScored = isObjectiveQuestion && answer.sensor_score !== null &&
                       (answer.sensor_feedback?.includes('Automatisk sensurert') || false);

  // Auto-save after 2 seconds of inactivity
  useEffect(() => {
    if (score === initialScore && feedback === (answer.sensor_feedback || '')) {
      return; // No changes
    }

    const scoreValue = parseFloat(score);
    if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > answer.max_points) {
      return; // Invalid score
    }

    const timer = setTimeout(() => {
      setSaveStatus('saving');
      setIsSaving(true);
      onSave(answer.answer_id, scoreValue, feedback, useAI);
      setTimeout(() => {
        setSaveStatus('saved');
        setIsSaving(false);
        setTimeout(() => setSaveStatus(null), 2000);
      }, 500);
    }, 2000);

    return () => clearTimeout(timer);
  }, [score, feedback]);

  const handleSubmit = () => {
    const scoreValue = parseFloat(score);
    if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > answer.max_points) {
      toast.showWarning(`Score må være mellom 0 og ${answer.max_points}`);
      return;
    }
    onSave(answer.answer_id, scoreValue, feedback, useAI);
  };

  const applyAISuggestion = () => {
    if (hasAI) {
      setScore(answer.ai_suggested_score!.toString());
      setFeedback(answer.ai_suggested_feedback || '');
      setUseAI(true);
    }
  };

  return (
    <div className={`bg-white border rounded-lg p-6 ${isGraded ? 'border-green-200' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm">
            {index + 1}
          </span>
          <div>
            <span className="text-xs text-gray-500 uppercase">
              {answer.question_type}
            </span>
            <p className="font-medium text-gray-900">Maks: {answer.max_points} poeng</p>
          </div>
        </div>
        {isGraded && (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Sensurert
          </span>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Spørsmål:</label>
          <p className="text-gray-900 bg-gray-50 p-3 rounded border">
            {answer.question_text}
          </p>
          {answer.grading_criteria && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="text-xs font-semibold text-amber-900 uppercase mb-1">
                Sensureringskriterier:
              </p>
              <p className="text-sm text-amber-900 whitespace-pre-wrap">
                {answer.grading_criteria}
              </p>
            </div>
          )}
        </div>

        {/* Statistics and Quality Assurance */}
        {questionStats && questionStats.graded_count > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <Info className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-indigo-900 mb-1">Statistikk for dette spørsmålet</h4>
                <p className="text-xs text-indigo-700">
                  Basert på {questionStats.graded_count} sensurerte svar av {questionStats.total_answers} totalt
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded p-2">
                <p className="text-xs text-gray-600">Gjennomsnitt</p>
                <p className="text-lg font-semibold text-indigo-900">
                  {questionStats.avg_score ? parseFloat(questionStats.avg_score).toFixed(1) : '0'}/{answer.max_points}
                </p>
                <p className="text-xs text-gray-500">
                  {questionStats.avg_score ? ((parseFloat(questionStats.avg_score) / answer.max_points) * 100).toFixed(0) : '0'}%
                </p>
              </div>
              <div className="bg-white rounded p-2">
                <p className="text-xs text-gray-600">Min - Maks</p>
                <p className="text-lg font-semibold text-indigo-900">
                  {questionStats.min_score ? parseFloat(questionStats.min_score).toFixed(1) : '0'} - {questionStats.max_score ? parseFloat(questionStats.max_score).toFixed(1) : '0'}
                </p>
              </div>
              {questionStats.std_dev && parseFloat(questionStats.std_dev) > 0 && (
                <div className="bg-white rounded p-2 col-span-2">
                  <p className="text-xs text-gray-600">Standardavvik</p>
                  <p className="text-lg font-semibold text-indigo-900">
                    ±{parseFloat(questionStats.std_dev).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {parseFloat(questionStats.std_dev) > (answer.max_points * 0.3) ?
                      '⚠️ Stor variasjon i sensurering' :
                      '✓ Konsistent sensurering'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Kandidatens svar:</label>
          {answer.question_type === 'written' ? (
            <p className="text-gray-900 bg-blue-50 p-3 rounded border border-blue-200 whitespace-pre-wrap">
              {answer.candidate_answer || 'Ingen svar gitt'}
            </p>
          ) : (
            <div className="bg-blue-50 p-3 rounded border border-blue-200">
              {answer.selected_option !== null && answer.options ? (
                <div className="space-y-2">
                  <p className="font-medium text-blue-900">
                    Valgt alternativ {answer.selected_option}:
                  </p>
                  <p className="text-gray-900">
                    {answer.options[answer.selected_option]?.text || 'Alternativ ikke funnet'}
                  </p>
                  <p className="text-sm mt-2">
                    {answer.options[answer.selected_option]?.is_correct ? (
                      <span className="text-green-700 font-medium">✓ Riktig svar</span>
                    ) : (
                      <span className="text-red-700 font-medium">✗ Feil svar</span>
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-gray-600">Ingen svar gitt</p>
              )}
            </div>
          )}
        </div>

        {isAutoScored && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-green-900 mb-1">Automatisk sensurert</h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-green-700">Poeng: </span>
                    <span className="font-semibold text-green-900">
                      {answer.sensor_score}/{answer.max_points}
                    </span>
                  </div>
                  <p className="text-sm text-green-800">
                    {answer.sensor_score === answer.max_points
                      ? '✓ Korrekt svar - full poengsum'
                      : '✗ Feil svar - ingen poeng'}
                  </p>
                  <p className="text-xs text-green-700 mt-2">
                    Objektive spørsmål sensureres automatisk. Du kan overstyre ved å endre poengsummen nedenfor.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasAI && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-purple-900 mb-1">KI-forslag</h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-purple-700">Poeng: </span>
                    <span className="font-semibold text-purple-900">
                      {answer.ai_suggested_score}/{answer.max_points}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-purple-700">Tilbakemelding: </span>
                    <p className="text-sm text-purple-900 mt-1">
                      {answer.ai_suggested_feedback}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={applyAISuggestion}
              className="mt-2 text-sm text-purple-700 hover:text-purple-900 underline"
            >
              Bruk KI-forslag
            </button>
          </div>
        )}

        {/* Only show grading fields for non-auto-scored questions */}
        {!isAutoScored && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Poeng *
                </label>
                <input
                  type="number"
                  min="0"
                  max={answer.max_points}
                  step="0.5"
                  value={score}
                  onChange={(e) => {
                    setScore(e.target.value);
                    setUseAI(false);
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={`0 - ${answer.max_points}`}
                />
              </div>
            </div>

            {/* Consistency Warning */}
            {questionStats && questionStats.graded_count > 2 && score && !isNaN(parseFloat(score)) && questionStats.avg_score && (
              (() => {
                const currentScore = parseFloat(score);
                const avgScore = parseFloat(questionStats.avg_score);
                const deviation = Math.abs(currentScore - avgScore);
                const stdDev = questionStats.std_dev ? parseFloat(questionStats.std_dev) : 0;
                const threshold = stdDev > 0 ? stdDev * 1.5 : answer.max_points * 0.3;

                if (deviation > threshold) {
                  const direction = currentScore > avgScore ? 'høyere' : 'lavere';
                  return (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-yellow-900">Konsistensadvarsel</h4>
                          <p className="text-sm text-yellow-800 mt-1">
                            Din score ({currentScore}) er betydelig {direction} enn gjennomsnittet ({avgScore?.toFixed(1)}).
                            Avvik: {deviation.toFixed(1)} poeng.
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Vurder om scoren er korrekt i forhold til kriteriene.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tilbakemelding (valgfritt)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value);
                  setUseAI(false);
                }}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Gi tilbakemelding til kandidaten..."
              />
            </div>

            <div className="space-y-2">
              {saveStatus && (
                <div className={`text-sm text-center ${saveStatus === 'saving' ? 'text-blue-600' : 'text-green-600'}`}>
                  {saveStatus === 'saving' ? '💾 Lagrer...' : '✓ Lagret'}
                </div>
              )}
            </div>
          </>
        )}

        {/* Show option to override auto-score if needed */}
        {isAutoScored && (
          <div className="mt-4">
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                Trenger du å overstyre automatisk sensurering?
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Poeng (overstyr)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={answer.max_points}
                    step="0.5"
                    value={score}
                    onChange={(e) => {
                      setScore(e.target.value);
                      setUseAI(false);
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={`0 - ${answer.max_points}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Begrunnelse for overstyring
                  </label>
                  <textarea
                    value={feedback}
                    onChange={(e) => {
                      setFeedback(e.target.value);
                      setUseAI(false);
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Forklar hvorfor du overstyrer automatisk sensurering..."
                  />
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={!score || isSaving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  Lagre overstyring
                </button>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
